import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import GameSummaryMobile from '../components/GameSummaryMobile';
import MobileNewsSummary from '../components/MobileNewsSummary';
import MobilePlaySummary from '../components/MobilePlaySummary';
import constants, { HIDDEN_TAGS } from '../../util/constants';
import { fetchGames } from '../../util/api/game';
import { firestore } from '../../firebase';
import { isNativeApp } from '../../util/platformDetection';

const MobilePortal = ({ user }) => {
  const history = useHistory();
  const location = useLocation();
  const [games, setGames] = useState([]);
  const [isLoadingGames, setIsLoadingGames] = useState(true);
  const [activeTab, setActiveTab] = useState('NEW');
  const [selectedDifficulty, setSelectedDifficulty] = useState(localStorage.getItem('portalDifficulty') || 'All Levels');
  const [selectedOpening, setSelectedOpening] = useState(null);
  const [isOpeningSelected, setIsOpeningSelected] = useState(false);
  
  // Pagination states
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Reference for scroll detection
  const scrollContainerRef = useRef(null);
  // Reference for tabs horizontal scroll container
  const tabsScrollRef = useRef(null);

  // Helper function to get tag key from tab name
  const getTagKeyFromTabName = (tabName) => {
    // console.log('🔍 getTagKeyFromTabName called with:', tabName);
    
    if (['NEW', 'FOLLOWING', 'TRENDING'].includes(tabName)) {
      // console.log('→ Special tab, returning null');
      return null;
    }

    // Direct mapping for special cases and uppercase button labels
    const directMappings = {
      'OPENINGS': 'Openings',
      'THE PROS': 'The-Pros',
      'FULL GAME': 'Full Game',
      // Add uppercase mappings for all visible tags
      'PUZZLE': 'Puzzle',
      'TACTICAL': 'Tactical', 
      'LESSON': 'Lesson',
      'BRILLIANCE': 'Brilliance',
      'CLASSICAL': 'Classical'
    };

    if (directMappings[tabName]) {
      // console.log('→ Direct mapping found:', directMappings[tabName]);
      return directMappings[tabName];
    }

    // Find by exact label match (case insensitive)
    const tagKey = Object.keys(constants.TAGS).find(
      key => constants.TAGS[key].label.toUpperCase() === tabName
    );

    if (tagKey) {
      // console.log('→ Label match found:', tagKey);
      return tagKey;
    }

    // Fallback: try to find by key name (case insensitive)
    const keyMatch = Object.keys(constants.TAGS).find(
      key => key.toUpperCase() === tabName
    );

    if (keyMatch) {
      // console.log('→ Key match found:', keyMatch);
      return keyMatch;
    }

    // console.log('→ No match found, returning null');
    return null;
  };

  // Fetch games based on active tab
  const fetchGamesForPortal = useCallback(async (gameLimit, tagKey = null, lastDoc = null, targetTab = null) => {
    // Use targetTab if provided (for tab switches), otherwise use current activeTab
    const currentTab = targetTab || activeTab;
    // console.log('fetchGamesForPortal called:', { activeTab, targetTab, currentTab, tagKey, gameLimit, lastDoc: !!lastDoc });
    try {
      if (!lastDoc) {
        // Clear games immediately when switching tabs (not pagination)
        setGames([]);
        setIsLoadingGames(true);
        // console.log('🧹 Tab switch: Cleared games state immediately');
      } else {
        setIsLoadingMore(true);
      }

      let gamesQuery;
      
      // Determine query type based on parameters, not activeTab state (which may be stale)
      // console.log('🎯 Query decision: currentTab =', currentTab, ', tagKey =', tagKey);
      
      if (tagKey) {
        // Tag-based query (OPENINGS, PUZZLE, TACTICAL, etc.)
        // console.log('📌 Using tag-based query for:', tagKey);
        
        // Special handling for Openings - need to handle compound tags like "Openings - Scandinavian Defense"
        if (tagKey === 'Openings') {
          // console.log('🔍 Special openings handling - fetching all games for client-side filtering');
          
          // Fetch more games to filter client-side (like desktop)
          gamesQuery = firestore.collection("games")
            .orderBy("createdAt", "desc")
            .limit(gameLimit * 5); // Fetch more games to filter from
          
          if (lastDoc) {
            gamesQuery = firestore.collection("games")
              .orderBy("createdAt", "desc")
              .startAfter(lastDoc)
              .limit(gameLimit * 5);
          }
        } else {
          // Normal tag query for other tags
          gamesQuery = firestore.collection("games")
            .where("tags", "array-contains", tagKey)
            .orderBy("createdAt", "desc")
            .limit(gameLimit);
          
          if (lastDoc) {
            gamesQuery = firestore.collection("games")
              .where("tags", "array-contains", tagKey)
              .orderBy("createdAt", "desc")
              .startAfter(lastDoc)
              .limit(gameLimit);
          }
        }
      } else if (currentTab === 'NEW') {
        gamesQuery = firestore.collection("games")
          .orderBy("createdAt", "desc")
          .limit(gameLimit);
        
        if (lastDoc) {
          gamesQuery = firestore.collection("games")
            .orderBy("createdAt", "desc")
            .startAfter(lastDoc)
            .limit(gameLimit);
        }
      } else if (currentTab === 'TRENDING') {
        // Calculate timestamp for 7 days ago (match desktop)
        const NOW = new Date();
        const DAYS_7 = 7 * 24 * 60 * 60 * 1000;
        const TIMESTAMP_7_DAYS_AGO = NOW.getTime() - DAYS_7;
        
        // console.log('📈 TRENDING: Using 7-day time window, timestamp:', TIMESTAMP_7_DAYS_AGO);
        
        gamesQuery = firestore.collection("games")
          .where("createdAt", ">", TIMESTAMP_7_DAYS_AGO)
          .where("score", ">", 0)
          .orderBy("createdAt", "desc")
          .orderBy("score", "desc")
          .limit(gameLimit);
        
        if (lastDoc) {
          gamesQuery = firestore.collection("games")
            .where("createdAt", ">", TIMESTAMP_7_DAYS_AGO)
            .where("score", ">", 0)
            .orderBy("createdAt", "desc")
            .orderBy("score", "desc")
            .startAfter(lastDoc)
            .limit(gameLimit);
        }
      } else if (currentTab === 'FOLLOWING') {
        if (!user.authenticated || !user.credentials?.handle) {
          // console.log('❌ FOLLOWING: User not authenticated or missing handle');
          setGames([]);
          setIsLoadingGames(false);
          setIsLoadingMore(false);
          return;
        }

        // console.log('👥 FOLLOWING: Getting followed users for handle:', user.credentials.handle);
        
        // Get users the current user follows (match desktop approach)
        const userSnapshot = await firestore.collection('users')
          .where('handle', '==', user.credentials.handle)
          .get();
          
        if (userSnapshot.empty) {
          // console.log('❌ FOLLOWING: User document not found');
          setGames([]);
          setIsLoadingGames(false);
          setIsLoadingMore(false);
          return;
        }
        
        const userData = userSnapshot.docs[0].data();
        const followedUsers = userData?.following || [];
        // console.log('👥 FOLLOWING: Found followed users:', followedUsers);

        if (followedUsers.length === 0) {
          // console.log('❌ FOLLOWING: No followed users');
          setGames([]);
          setIsLoadingGames(false);
          setIsLoadingMore(false);
          return;
        }

        // Use batching like desktop (Firestore allows max 10 items in "in" clause)
        const batchSize = 10;
        const batch = followedUsers.slice(0, batchSize); // For simplicity, use first batch only for now
        
        // console.log('🎯 FOLLOWING: Querying games by creatorHandle in batch:', batch);
        gamesQuery = firestore.collection("games")
          .where("creatorHandle", "in", batch)
          .orderBy("createdAt", "desc")
          .limit(gameLimit);
        
        if (lastDoc) {
          gamesQuery = firestore.collection("games")
            .where("creatorHandle", "in", batch)
            .orderBy("createdAt", "desc")
            .startAfter(lastDoc)
            .limit(gameLimit);
        }
      }

      const gamesSnapshot = await gamesQuery.get();
      // console.log('📊 Query executed, got docs:', gamesSnapshot.size);
      
      // Log first few games for verification
      if (gamesSnapshot.size > 0) {
        // console.log('📋 First few games found:');
        gamesSnapshot.docs.slice(0, 3).forEach((doc, index) => {
          const data = doc.data();
          // console.log(`  ${index + 1}. Game ${doc.id}: tags=[${data.tags?.join(', ') || 'none'}], creatorHandle="${data.creatorHandle || 'missing'}", creator="${data.creator || 'missing'}"`);
        });
      }
      
      const newGames = [];
      let lastVisible = null;

      gamesSnapshot.forEach((doc) => {
        const gameData = {
          id: doc.id,
          ...doc.data()
        };
        
        // Special filtering for Openings tab
        if (tagKey === 'Openings') {
          // Filter to only include games with opening-related tags (like desktop)
          if (gameData.tags && Array.isArray(gameData.tags)) {
            const hasOpeningTag = gameData.tags.some(tag =>
              typeof tag === 'string' && (
                tag === 'Openings' ||
                tag.startsWith('Openings -') ||
                tag.startsWith('Openings-')
              )
            );
            
            if (hasOpeningTag) {
              newGames.push(gameData);
              // console.log('✅ Found opening game:', doc.id, 'tags:', gameData.tags);
            } else {
              // console.log('❌ Skipped non-opening game:', doc.id, 'tags:', gameData.tags);
            }
          }
        } else {
          // Normal processing for other tags
          newGames.push(gameData);
        }
        lastVisible = doc;
      });

      if (!lastDoc) {
        setGames(newGames);
      } else {
        setGames(prevGames => [...prevGames, ...newGames]);
      }

      // console.log('New games processed:', newGames.length);
      setNextCursor(lastVisible);
      
      // For openings, we need different logic since we filter client-side
      if (tagKey === 'Openings') {
        // Continue pagination if we didn't get enough opening games and there might be more
        setHasMore(gamesSnapshot.size === gameLimit * 5 && newGames.length < gameLimit);
      } else {
        setHasMore(newGames.length === gameLimit);
      }
    } catch (error) {
      console.error("Error fetching games:", error);
    } finally {
      setIsLoadingGames(false);
      setIsLoadingMore(false);
    }
  }, [activeTab, user]);

  // Load more games on scroll
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || isLoadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      const tagKey = getTagKeyFromTabName(activeTab);
      fetchGamesForPortal(10, tagKey, nextCursor);
    }
  }, [isLoadingMore, hasMore, nextCursor, activeTab, fetchGamesForPortal]);

  // Handle tab change
  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setNextCursor(null);
    setHasMore(true);
    // Note: setGames([]) and portalGameIdTracker.clear() now handled in fetchGamesForPortal
    
    const tagKey = getTagKeyFromTabName(tab);
    fetchGamesForPortal(10, tagKey, null, tab); // Pass target tab directly
  };

  // Check for location.state when component mounts (copy from desktop portal)
  useEffect(() => {
    // console.log('MobilePortal useEffect - location.state:', location.state);
    
    if (location.state && location.state.activeTag) {
      // console.log('MobilePortal: Received activeTag from location.state:', location.state.activeTag);
      // console.log('MobilePortal: Received filterTag from location.state:', location.state.filterTag);
      
      // Set the active tab to the one from navigation state (convert to uppercase to match expected format)
      const upperCaseActiveTag = location.state.activeTag.toUpperCase();
      setActiveTab(upperCaseActiveTag);

      // Auto-scroll to make the active tab visible
      setTimeout(() => {
        if (tabsScrollRef.current) {
          // Find the active tab button
          const tabButtons = tabsScrollRef.current.querySelectorAll('button');
          const activeButton = Array.from(tabButtons).find(button => 
            button.textContent.trim() === upperCaseActiveTag
          );
          
          if (activeButton) {
            // console.log('MobilePortal: Auto-scrolling to make', upperCaseActiveTag, 'tab visible');
            // Scroll to show the active tab at the left edge
            const containerRect = tabsScrollRef.current.getBoundingClientRect();
            const buttonRect = activeButton.getBoundingClientRect();
            const scrollLeft = activeButton.offsetLeft - 20; // 20px padding from left edge
            
            tabsScrollRef.current.scrollTo({
              left: scrollLeft,
              behavior: 'smooth'
            });
          }
        }
      }, 100); // Small delay to ensure DOM is updated

      // Check if we have a separate filterTag (for cases where case sensitivity matters)
      const tagToFilter = location.state.filterTag || location.state.activeTag;
      // console.log('MobilePortal: tagToFilter:', tagToFilter);

      // Find the tag key that matches this label
      const tagKey = Object.keys(constants.TAGS).find(
        key => constants.TAGS[key].label === tagToFilter
      );
      // console.log('MobilePortal: Found tagKey:', tagKey);

      // Fetch games with this tag
      if (tagKey) {
        // console.log('MobilePortal: Fetching games with tagKey:', tagKey);
        fetchGamesForPortal(10, tagKey);
      } else {
        // console.log('MobilePortal: No tagKey found, using tagToFilter directly:', tagToFilter);
        fetchGamesForPortal(10, tagToFilter);
      }

      // Clear the location state to prevent re-applying on refresh
      window.history.replaceState({}, document.title);
      return;
    }

    // Initial load when no specific state is passed
    fetchGamesForPortal(10);
  }, [location]);

  // Function to create mobile games list with injection components
  const createMobileGamesList = (games) => {
    const result = [];
    
    games.forEach((game, index) => {
      // Add the game
      result.push({ type: 'game', data: game, key: game.id });
      
      // Inject MobileNewsSummary every 1 + 10 (so after games 1, 11, 21, 31, etc.)
      if ((index + 1) % 10 === 1) {
        result.push({ 
          type: 'injection', 
          component: 'MobileNewsSummary',
          key: `news-injection-${index}` 
        });
      }
    });
    
    return result;
  };

  // Render tags horizontal scroll
  const renderTags = () => (
    <div style={{
      backgroundColor: '#1a1a1a',
      borderBottom: '1px solid #333',
      position: 'sticky',
      top: 0,
      zIndex: 100
    }}>
      <div 
        ref={tabsScrollRef}
        style={{
          display: 'flex',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' }
        }}>
        {[
          'NEW', 'FOLLOWING', 'TRENDING',
          ...Object.keys(constants.TAGS)
            .filter(tag => !HIDDEN_TAGS.includes(tag))
            .map(tag => constants.TAGS[tag].label)
        ].map((tab) => {
          const displayTab = tab === 'Openings' ? 'OPENINGS' : tab.toUpperCase();
          const isActive = displayTab === activeTab || 
            displayTab.toUpperCase() === activeTab.toUpperCase() ||
            (tab === 'Openings' && activeTab === 'OPENINGS') ||
            (Object.keys(constants.TAGS).some(key =>
              constants.TAGS[key].label === tab && (key.toUpperCase() === activeTab || key === activeTab))
            );

          return (
            <button
              key={tab}
              onClick={() => handleTabClick(displayTab)}
              style={{
                padding: '12px 20px',
                border: 'none',
                backgroundColor: isActive ? 'gold' : 'transparent',
                color: isActive ? 'black' : '#888',
                fontSize: '14px',
                fontWeight: isActive ? 'bold' : 'normal',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                textTransform: 'uppercase'
              }}
            >
              {displayTab}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div style={{
      width: '100vw',
      height: 'calc(100dvh - 55px)', // Account for bottom navigation
      backgroundColor: '#1a1a1a',
      display: 'flex',
      flexDirection: 'column',
      paddingTop: 'env(safe-area-inset-top)',
      paddingBottom: 'env(safe-area-inset-bottom)' // iPhone gesture area
    }}>
      {/* Tags horizontal scroll */}
      {renderTags()}

      {/* Games grid */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        style={{
          flex: 1,
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          padding: '10px'
        }}
      >
        {isLoadingGames ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '200px',
            color: '#666'
          }}>
            <div style={{ textAlign: 'center' }}>
              <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '24px' }}></i>
            </div>
          </div>
        ) : games.length === 0 ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '200px',
            color: '#666',
            textAlign: 'center',
            padding: '20px'
          }}>
            <div>
              <div style={{ fontSize: '48px', marginBottom: '20px', color: '#666' }}>
                <i className="fa-solid fa-chess-pawn"></i>
              </div>
              <h3 style={{ color: '#888', marginBottom: '10px' }}>No Games Found</h3>
              <button
                onClick={() => history.push('/Analysis')}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#666',
                  fontSize: '14px',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: '0'
                }}
              >
                Start Creating
              </button>
            </div>
          </div>
        ) : (
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '10px'
            }}>
              {createMobileGamesList(games).map(item => {
                if (item.type === 'game') {
                  return (
                    <GameSummaryMobile
                      key={item.key}
                      gameData={item.data}
                      boardWidth={(window.innerWidth - 40) / 2 + 4} // Container width divided by 2 columns minus padding, plus 4px
                      showDetails={true}
                      onClick={() => {
                        // Pass the current tab's gameIds for navigation
                        const gameIds = games.map(g => g.id);
                        
                        // Add URL parameter for tag filters (copy from desktop portal)
                        const specialTabs = ['NEW', 'FOLLOWING', 'TRENDING'];
                        let queryParams = '';
                        if (!specialTabs.includes(activeTab)) {
                          const selectedTagKey = getTagKeyFromTabName(activeTab);
                          queryParams = selectedTagKey ? `?tag=${selectedTagKey}` : '';
                        }
                        
                        // console.log('MobilePortal: Navigating to game with queryParams:', queryParams);
                        
                        history.push(`/games/${item.data.id}${queryParams}`, { 
                          origin: activeTab === 'TRENDING' ? 'TRENDING' : 
                                  activeTab === 'FOLLOWING' ? 'FOLLOWING' : 'PORTAL',
                          gameIds: gameIds,
                          returnLabel: `Back to ${activeTab}`,
                          tab: activeTab
                        });
                      }}
                    />
                  );
                } else if (item.type === 'injection') {
                  // Render injection component with squared styling
                  const InjectionComponent = item.component === 'MobileNewsSummary' ? MobileNewsSummary : MobilePlaySummary;
                  const borderColor = item.component === 'MobileNewsSummary' ? 'gold' : '#58b047';
                  
                  return (
                    <div
                      key={item.key}
                      style={{
                        cursor: 'pointer',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        backgroundColor: '#1a1a1a',
                        border: `3px solid ${borderColor}`,
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <InjectionComponent />
                    </div>
                  );
                }
                return null;
              })}
            </div>

            {/* Loading more indicator */}
            {isLoadingMore && (
              <div style={{
                textAlign: 'center',
                padding: '20px',
                color: '#666'
              }}>
                <i className="fa-solid fa-spinner fa-spin"></i> Loading more...
              </div>
            )}

            {/* No more games message */}
            {!hasMore && games.length > 0 && (
              <div style={{
                textAlign: 'center',
                padding: '20px',
                color: '#666'
              }}>
                No more games to load
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user
});

export default connect(mapStateToProps)(MobilePortal);